/**
 * The application shell: every surface the reader can reach, rendered once at
 * start-up and then filled in by `main.ts`.
 *
 * It lives apart from the behaviour so the markup can be rendered on its own —
 * the accessibility checks mount it in a bare document and audit each surface
 * without a worker, a session, or a network.
 */
export const SHELL_HTML = `
  <main class="auth-gate" data-auth-gate>
    <section class="auth-story" aria-labelledby="auth-title">
      <a class="auth-brand" href="https://recallvault.dev"><span class="brand-mark" aria-hidden="true">R</span><span>RecallVault</span></a>
      <div class="auth-copy">
        <p class="eyebrow">Private memory, ready everywhere</p>
        <h1 id="auth-title">Your AI writes it.<br />You can always read it.</h1>
        <p>An encrypted, offline-ready vault your assistant fills in over MCP—and that you can search, verify, and roll back yourself.</p>
      </div>
      <p class="auth-footnote">Your vault stays partitioned by account. RecallVault never joins identities just because their email addresses match.</p>
    </section>
    <section class="auth-panel" aria-label="Sign in to RecallVault">
      <div class="auth-card">
        <p class="eyebrow">Welcome</p>
        <h2>Open your vault</h2>
        <p>Use the account you trust. You can link another provider later from settings.</p>
        <div class="provider-buttons" role="group" aria-label="Choose a sign-in provider" data-provider-buttons>
          <div id="appleid-signin" data-mode="center-align" data-color="black" data-border="true" data-type="sign-in" data-border-radius="5" data-width="360" data-height="44"></div>
          <div class="google-sign-in" data-google-sign-in></div>
        </div>
        <div class="auth-progress" role="status" aria-live="polite" data-auth-status>Checking your session…</div>
        <button class="retry-auth" type="button" data-action="retry-auth" hidden>Try again</button>
        <p class="auth-legal">By continuing, you agree to keep your account credentials secure. Provider tokens are exchanged for a private RecallVault session and are not stored in this browser.</p>
      </div>
    </section>
  </main>
  <a class="skip-link" href="#reader">Skip to the note</a>
  <div class="app-frame" data-app-frame hidden>
    <header class="topbar">
      <div class="topbar-lead">
        <button class="brand" type="button" data-action="home" aria-label="RecallVault home">
          <span class="brand-mark" aria-hidden="true">R</span><span>RecallVault</span>
        </button>
        <button class="rail-toggle" type="button" data-action="toggle-nav-rail" aria-controls="vault-rail" aria-expanded="true" aria-label="Hide the notes rail" title="Notes rail ([)"><span aria-hidden="true">◧</span></button>
      </div>
      <button class="search-trigger" type="button" data-action="open-search">
        <span aria-hidden="true">⌕</span><span>Search this device</span><kbd>/</kbd>
      </button>
      <div class="top-actions">
        <button class="sync-button" type="button" data-action="sync">
          <span data-sync-icon>↻</span>
          <span class="sync-lines"><span data-sync-label>Local</span><span class="connection-state" data-connection-state role="status" aria-live="polite">Hosted vault not checked</span></span>
        </button>
        <button class="avatar" type="button" data-action="account" aria-label="Account and storage">K</button>
        <button class="rail-toggle" type="button" data-action="toggle-context-rail" aria-controls="context-rail" aria-expanded="true" aria-label="Hide the context rail" title="Context rail (])"><span aria-hidden="true">◨</span></button>
      </div>
    </header>

    <aside class="sidebar" id="vault-rail" aria-label="Vault navigation">
      <div class="ai-callout">
        <strong>Your AI writes here</strong>
        <p>Ask the assistant connected to this vault to create or change notes. Sync to read what it wrote.</p>
        <button class="ask-button" type="button" data-action="ask"><span aria-hidden="true">✦</span> Ask your AI</button>
      </div>
      <nav class="primary-nav" aria-label="Primary">
        <button class="nav-item active" type="button" data-go="vault"><span>◫</span>All notes <span class="count" data-note-count>0</span></button>
        <button class="nav-item" type="button" data-go="vault?filter=recent"><span>◷</span>Recently changed</button>
        <button class="nav-item" type="button" data-go="vault?filter=queued"><span>⇡</span>Waiting to sync <span class="count" data-queue-count>0</span></button>
        <button class="nav-item" type="button" data-go="vault?filter=conflict"><span>◇</span>Needs attention <span class="count attention" data-conflict-count>0</span></button>
      </nav>
      <div class="sidebar-heading"><span>Your AI's work</span></div>
      <nav class="primary-nav" aria-label="Domain objects">
        <button class="nav-item" type="button" data-go="alerts"><span>◈</span>Alerts <span class="count attention" data-alert-count>0</span></button>
        <button class="nav-item" type="button" data-go="questions"><span>◉</span>Questions <span class="count attention" data-question-count>0</span></button>
        <button class="nav-item" type="button" data-go="alerts/history"><span>◴</span>Alert history</button>
        <button class="nav-item" type="button" data-go="sources"><span>⌂</span>Sources <span class="count" data-source-count>0</span></button>
      </nav>
      <p class="counts-note" data-counts-note></p>
      <div class="sidebar-heading"><span>Notes</span><button type="button" data-action="collapse-folders" aria-label="Collapse every folder" title="Collapse every folder">⌃</button></div>
      <div class="note-list" role="tree" aria-label="Notes by folder" data-note-list></div>
      <div class="sidebar-footer">
        <a class="nav-item" href="https://recallvault.dev"><span>?</span>About RecallVault</a>
        <button class="nav-item" type="button" data-action="install" hidden><span>↓</span>Install app</button>
      </div>
    </aside>

    <main class="workspace" id="reader" tabindex="-1">
      <section class="welcome" data-welcome hidden>
        <p class="eyebrow">An AI-operated vault</p>
        <h1 data-welcome-title>Nothing written yet.</h1>
        <p data-welcome-copy></p>
        <div class="welcome-actions">
          <button class="primary-button" type="button" data-action="ask">Ask your AI to write one</button>
          <button class="quiet-button" type="button" data-action="sync">Sync and check again</button>
        </div>
        <p class="welcome-activity" data-ai-activity hidden></p>
        <p class="welcome-freshness" data-freshness></p>
      </section>

      <section class="domain" data-domain hidden aria-live="polite">
        <div class="reader-toolbar">
          <div class="breadcrumb" data-domain-breadcrumb></div>
          <div class="reader-actions">
            <span class="state-pill" data-domain-pill></span>
            <button class="quiet-button" type="button" data-go="vault">Back to notes</button>
          </div>
        </div>
        <header class="reader-head">
          <h1 data-domain-title></h1>
          <p class="reader-meta" data-domain-meta></p>
        </header>
        <div class="domain-body" data-domain-body></div>
      </section>

      <section class="reader" data-reader hidden>
        <div class="reader-toolbar">
          <div class="breadcrumb" data-breadcrumb></div>
          <div class="reader-actions">
            <span class="state-pill" data-state-pill></span>
            <button class="revise-button" type="button" data-action="ask-revise"><span aria-hidden="true">✦</span> Ask AI to change this</button>
            <button class="icon-button" type="button" data-action="history" aria-label="Version history">◴</button>
            <button class="icon-button" type="button" data-action="toggle-source" aria-label="Show Markdown source" aria-pressed="false">◐</button>
          </div>
        </div>
        <header class="reader-head">
          <h1 data-reader-title></h1>
          <p class="reader-meta" data-reader-meta></p>
          <dl class="reader-frontmatter" data-reader-frontmatter hidden></dl>
        </header>
        <div class="reading-area">
          <article class="markdown-preview" data-preview></article>
          <pre class="markdown-source" data-source hidden></pre>
        </div>
        <footer class="reader-footer">
          <span data-read-message></span>
          <span data-read-hint>Read-only · your AI makes the changes</span>
        </footer>
      </section>

    </main>

    <aside class="context-panel" id="context-rail" aria-label="Vault state">
      <section>
        <div class="panel-heading"><h2>Connected notes</h2><span data-backlink-count>0</span></div>
        <div class="backlinks" data-backlinks><p class="muted">Links to this note will appear here.</p></div>
      </section>
      <section>
        <div class="panel-heading"><h2>Activity</h2><button class="text-button" type="button" data-action="sync">Sync</button></div>
        <div class="activity" data-activity><p class="muted">Nothing has changed on this device yet.</p></div>
      </section>
      <section>
        <div class="panel-heading"><h2>Storage</h2><button class="text-button" type="button" data-action="account">Details</button></div>
        <div class="capability-card" data-capabilities>
          <span class="pulse-dot"></span><div><strong>Opening your vault…</strong><p>Checking offline storage</p></div>
        </div>
      </section>
      <section>
        <div class="panel-heading"><h2>Recent receipts</h2></div>
        <div class="receipts" data-receipts><p class="muted">No sync attempts yet.</p></div>
      </section>
      <section>
        <div class="panel-heading"><h2>Session log</h2></div>
        <ol class="trace" data-trace></ol>
      </section>
    </aside>

    <nav class="mobile-nav" aria-label="Mobile navigation">
      <button type="button" data-action="home"><span>◫</span>Vault</button>
      <button type="button" data-action="open-search"><span>⌕</span>Search</button>
      <button class="mobile-capture" type="button" data-action="ask"><span>✦</span>Ask AI</button>
      <button type="button" data-action="sync"><span>↻</span>Sync</button>
      <button type="button" data-action="account"><span>○</span>You</button>
    </nav>
  </div>

  <dialog class="dialog search-dialog" data-search-dialog>
    <form method="dialog" class="dialog-frame">
      <header><span aria-hidden="true">⌕</span><label class="sr-only" for="vault-search">Search notes</label><input id="vault-search" data-search-input placeholder="Search every word on this device…" autocomplete="off" /><button value="cancel" aria-label="Close">×</button></header>
      <div class="search-modes" role="tablist" aria-label="Search mode"><button type="button" class="active" role="tab" id="search-tab-exact" aria-controls="search-results" aria-selected="true" tabindex="0" data-search-mode="exact">Exact · on this device</button><button type="button" role="tab" id="search-tab-semantic" aria-controls="search-results" aria-selected="false" tabindex="-1" data-search-mode="semantic"><span class="online-dot"></span>Meaning · online</button></div>
      <div class="search-status" data-search-status>Type to search titles, paths, and Markdown.</div>
      <div class="search-results" id="search-results" role="tabpanel" aria-labelledby="search-tab-exact" data-search-results></div>
      <footer><span><kbd>↑</kbd> <kbd>↓</kbd> move · <kbd>↵</kbd> open</span><strong data-search-privacy>Nothing leaves this device.</strong></footer>
    </form>
  </dialog>

  <dialog class="dialog ask-dialog" data-ask-dialog>
    <form method="dialog" class="ask-card">
      <button class="dialog-close" value="cancel" aria-label="Close">×</button>
      <p class="eyebrow">Ask your AI</p>
      <h2 data-ask-title>Ask your AI to write this note</h2>
      <p data-ask-explanation></p>
      <label class="sr-only" for="ask-prompt">Prompt to copy</label>
      <textarea class="ask-prompt" id="ask-prompt" data-ask-prompt rows="4"></textarea>
      <label for="ask-file">Attach a file</label>
      <input id="ask-file" data-ask-file type="file">
      <p class="fine-print" data-ask-attachment>Optional: one file, up to 40 MiB.</p>
      <div class="ask-actions">
        <button class="primary-button" type="button" data-action="submit-ask">Submit for later</button>
        <button class="text-button" type="button" data-ask-discard>Start a new request</button>
        <button class="text-button" type="button" data-action="copy-prompt">Copy prompt instead</button>
        <span class="fine-print" data-ask-copied role="status" aria-live="polite"></span>
      </div>
      <p class="fine-print">Submitted requests wait durably for the scheduled assistant. Copying remains available if you need to act immediately.</p>
    </form>
  </dialog>

  <dialog class="dialog account-dialog" data-account-dialog>
    <form method="dialog" class="account-card">
      <button class="dialog-close" value="cancel" aria-label="Close">×</button>
      <p class="eyebrow">Account & resilience</p>
      <h2 data-account-title>Local vault</h2>
      <p data-account-copy>Your notes are partitioned into a private hosted account with an offline cache on this browser.</p>
      <div class="account-storage" data-account-storage></div>
      <p class="account-activity" data-account-activity hidden></p>
      <button class="primary-button full" type="button" data-action="persist">Protect offline storage</button>
      <div class="identity-links"><p>Connect another sign-in</p><button class="apple-link" type="button" data-action="link-apple"> Apple</button><div data-google-link></div></div>
      <section class="transfer-section">
        <p>Move your vault</p>
        <p class="fine-print">Export takes a copy of everything on this device. Import carries notes in from another vault or a backup — it is bulk migration, not a way to write a note. New notes come from asking your AI.</p>
        <div class="account-transfer"><button class="secondary-link" type="button" data-action="export">Export vault</button><button class="secondary-link" type="button" data-action="import">Import notes</button></div>
        <input type="file" data-import-input accept=".md,.markdown,.json,text/markdown,application/json" multiple hidden />
      </section>
      <section class="extension-section" aria-labelledby="extensions-title">
        <h3 id="extensions-title">Extensions</h3>
        <p class="fine-print">Turn an extension off to hide its tools. Your settings, connections and data stay saved for when you turn it back on.</p>
        <div data-account-extensions></div>
      </section>
      <section class="push-section">
        <p>Notifications</p>
        <p class="fine-print" data-push-status>Get an alert here when a run fails or a reply arrives, even with RecallVault closed.</p>
        <button class="secondary-link" type="button" data-action="enable-push">Turn on alert notifications</button>
      </section>
      <section class="release-section">
        <p>This page is running <span data-release>Development build</span>.</p>
        <p class="fine-print">If the app looks wrong or will not update, discard the offline copy. It removes the cached shell and reloads from the server. Your notes are not touched.</p>
        <button class="secondary-link" type="button" data-action="discard-shell">Discard the offline copy and reload</button>
      </section>
      <button class="danger-link" type="button" data-action="logout">Sign out and close this vault</button>
      <p class="fine-print">Signing out switches partitions immediately. It never merges one account’s local cache into another.</p>
    </form>
  </dialog>

  <dialog class="dialog history-dialog" data-history-dialog>
    <form method="dialog" class="history-card">
      <header><div><p class="eyebrow">Every save is recoverable</p><h2>Version history</h2></div><button value="cancel" aria-label="Close">×</button></header>
      <p class="fine-print">Restoring writes the older text back as a new version. Nothing is erased—the current text stays in this list.</p>
      <div data-history-list></div>
    </form>
  </dialog>

  <div class="update-bar" role="status" aria-live="polite" data-update-bar hidden>
    <p data-update-message></p>
    <div class="update-actions">
      <button class="primary-button" type="button" data-action="reload-update">Reload</button>
      <button class="text-button" type="button" data-action="dismiss-update">Not now</button>
    </div>
  </div>
  <div class="toast" role="status" aria-live="polite" data-toast></div>
`;
